/**
 * Browser-only, no-send session capture used to derive a request budget from a
 * real five-minute navigation.  It intentionally stores aggregates rather than
 * URLs with query strings, cookies, account IDs, or event payloads.  The
 * capture is opt-in and remains in memory until the operator exports it.
 */

export const SESSION_CAPTURE_VERSION = 1;
export const SESSION_CAPTURE_DURATION_MS = 5 * 60_000;
export const SESSION_CAPTURE_MAX_EVENTS = 2_048;
export const SESSION_CAPTURE_MAX_ENDPOINTS = 100;

const INTERACTION_TYPES = new Set([
  "navigation",
  "search",
  "map",
  "favorite",
  "account",
  "service_request",
  "messaging",
  "idle",
  "other",
]);

type EndpointCounters = {
  requests: number;
  errors: number;
  idleRequests: number;
  pollingRequests: number;
  duplicateConcurrentRequests: number;
  totalDurationMs: number;
  samplesMs: number[];
};

type RequestRecord = {
  endpoint: string;
  startedAtMs: number;
  durationMs: number;
  status: number | null;
  idle: boolean;
  polling: boolean;
  duplicateConcurrent: boolean;
  pageCategory: string;
  interaction: string;
};

export type SessionCaptureOptions = {
  now?: () => number;
  durationMs?: number;
  maxEvents?: number;
};

export type SessionCaptureSummary = {
  version: number;
  durationSeconds: number;
  complete: boolean;
  requests: number;
  errors: number;
  errorRate: number;
  requestsPerSessionMinute: number;
  pageTransitions: number;
  pageTransitionsPerMinute: number;
  interactions: number;
  interactionsPerMinute: number;
  idleRequests: number;
  idleRequestsPerMinute: number;
  pollingRequests: number;
  pollingRequestsPerMinute: number;
  duplicateConcurrentRequests: number;
  duplicateConcurrentRate: number;
  latencyMs: {
    p50: number | null;
    p95: number | null;
    p99: number | null;
    max: number | null;
  };
  interactionsByType: Record<string, number>;
  pageCategories: Record<string, number>;
  endpoints: Record<
    string,
    {
      requests: number;
      errors: number;
      errorRate: number;
      idleRequests: number;
      pollingRequests: number;
      duplicateConcurrentRequests: number;
      latencyMs: {
        p50: number | null;
        p95: number | null;
        p99: number | null;
        max: number | null;
      };
    }
  >;
  capture: {
    eventSamples: number;
    eventSampleLimit: number;
    requestsObservedFrom: "resource-timing";
    networkSentByCapture: false;
    userIdentifiersStored: false;
  };
};

export type SessionCaptureController = {
  readonly startedAtMs: number;
  recordRequest(input: {
    url: string;
    startedAtMs?: number;
    durationMs: number;
    status?: number | null;
    pageCategory?: string;
    interaction?: string;
    idle?: boolean;
    polling?: boolean;
  }): void;
  recordPageTransition(path: string, atMs?: number): void;
  recordInteraction(type: string, atMs?: number): void;
  snapshot(nowMs?: number): SessionCaptureSummary;
  stop(): void;
};

function percentile(values: number[], fraction: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left - right);
  return sorted[
    Math.min(sorted.length - 1, Math.ceil(sorted.length * fraction) - 1)
  ]!;
}

function latencySummary(values: number[]) {
  return {
    p50: percentile(values, 0.5),
    p95: percentile(values, 0.95),
    p99: percentile(values, 0.99),
    max: values.length > 0 ? Math.max(...values) : null,
  };
}

function remember(values: number[], value: number, max: number): void {
  if (values.length < max) {
    values.push(value);
    return;
  }
  values[Math.floor(values.length / 2) % max] = value;
}

function boundedLabel(value: string | undefined, fallback: string): string {
  const normalized = value?.trim().toLowerCase().replace(/[^a-z0-9_:-]/g, "");
  return normalized && INTERACTION_TYPES.has(normalized) ? normalized : fallback;
}

export function pageCategoryFromPath(path: string): string {
  const first = path.replace(/^\/+/, "").split("/", 1)[0] ?? "";
  if (!first) return "home";
  if (first === "fbos") return "fbo";
  if (first === "airports") return "airport";
  if (first === "flight-schools") return "flight_school";
  if (first === "services") return "service";
  if (first === "maintenance") return "maintenance";
  if (first === "favorites") return "favorites";
  if (first === "account") return "account";
  if (first === "requests" || first === "service-requests") {
    return "service_request";
  }
  if (first === "messages" || first === "inbox") return "messaging";
  if (first === "map") return "map";
  return "other";
}

function endpointFromUrl(rawUrl: string): string | null {
  try {
    const browserOrigin =
      typeof window !== "undefined" ? window.location.origin : null;
    const url = new URL(rawUrl, browserOrigin ?? "https://aero.local");
    if (
      (browserOrigin !== null && url.origin !== browserOrigin) ||
      !url.pathname.startsWith("/api/")
    ) {
      return null;
    }
    return url.pathname
      .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id")
      .replace(/\/\d+(?=\/|$)/g, "/:id")
      .replace(/\/[A-Z0-9]{3,4}(?=\/|$)/g, "/:code")
      .replace(/\/[^/]{48,}(?=\/|$)/g, "/:token")
      .slice(0, 180);
  } catch {
    return null;
  }
}

export function createSessionCapture(
  options: SessionCaptureOptions = {},
): SessionCaptureController {
  const now = options.now ?? (() => performance.now());
  const startedAtMs = now();
  const durationMs = Math.max(
    1_000,
    Math.min(SESSION_CAPTURE_DURATION_MS, options.durationMs ?? SESSION_CAPTURE_DURATION_MS),
  );
  const maxEvents = Math.max(
    1,
    Math.min(SESSION_CAPTURE_MAX_EVENTS, options.maxEvents ?? SESSION_CAPTURE_MAX_EVENTS),
  );
  const requests: RequestRecord[] = [];
  const endpoints = new Map<string, EndpointCounters>();
  const interactions = new Map<string, number>();
  const pageCategories = new Map<string, number>();
  const active = new Map<string, { startedAtMs: number; endsAtMs: number }[]>();
  let pageTransitions = 0;
  let lastInteractionAtMs = startedAtMs;
  let totalRequests = 0;
  let totalErrors = 0;
  let totalIdleRequests = 0;
  let totalPollingRequests = 0;
  let totalDuplicateConcurrentRequests = 0;
  let stopped = false;

  const routeFor = (rawUrl: string): string | null => endpointFromUrl(rawUrl);

  const recordInteraction = (type: string, atMs = now()): void => {
    const label = boundedLabel(type, "other");
    interactions.set(label, (interactions.get(label) ?? 0) + 1);
    lastInteractionAtMs = atMs;
  };

  const recordPageTransition = (path: string, atMs = now()): void => {
    if (stopped || atMs - startedAtMs > durationMs) return;
    const category = pageCategoryFromPath(path);
    pageCategories.set(category, (pageCategories.get(category) ?? 0) + 1);
    pageTransitions += 1;
    recordInteraction("navigation", atMs);
  };

  const recordRequest = (input: {
    url: string;
    startedAtMs?: number;
    durationMs: number;
    status?: number | null;
    pageCategory?: string;
    interaction?: string;
    idle?: boolean;
    polling?: boolean;
  }): void => {
    if (stopped) return;
    const endpoint = routeFor(input.url);
    if (!endpoint) return;
    const requestStartedAtMs = input.startedAtMs ?? now();
    const requestDurationMs = Number.isFinite(input.durationMs)
      ? Math.max(0, Math.min(120_000, input.durationMs))
      : 0;
    if (
      requestStartedAtMs < startedAtMs ||
      requestStartedAtMs - startedAtMs > durationMs
    ) {
      return;
    }
    const endAtMs = requestStartedAtMs + requestDurationMs;
    const previous = active.get(endpoint) ?? [];
    const duplicateConcurrent = previous.some(
      (entry) =>
        entry.startedAtMs < endAtMs &&
        requestStartedAtMs < entry.endsAtMs,
    );
    active.set(
      endpoint,
      [
        ...previous.filter((entry) => entry.endsAtMs > requestStartedAtMs),
        { startedAtMs: requestStartedAtMs, endsAtMs: endAtMs },
      ],
    );
    const hidden =
      typeof document !== "undefined" && document.visibilityState === "hidden";
    const idle =
      input.idle ??
      (hidden || requestStartedAtMs - lastInteractionAtMs >= 60_000);
    const interaction = boundedLabel(input.interaction, idle ? "idle" : "other");
    const polling =
      input.polling ??
      (idle || /(?:poll|heartbeat|notifications|unread|status)/i.test(endpoint));
    const status = input.status ?? null;
    const record: RequestRecord = {
      endpoint,
      startedAtMs: requestStartedAtMs,
      durationMs: requestDurationMs,
      status,
      idle,
      polling,
      duplicateConcurrent,
      pageCategory: input.pageCategory ?? "other",
      interaction,
    };
    totalRequests += 1;
    totalErrors +=
      status !== null && (status < 200 || status >= 400) ? 1 : 0;
    totalIdleRequests += idle ? 1 : 0;
    totalPollingRequests += polling ? 1 : 0;
    totalDuplicateConcurrentRequests += duplicateConcurrent ? 1 : 0;
    if (requests.length < maxEvents) requests.push(record);

    let counters = endpoints.get(endpoint);
    if (!counters) {
      if (endpoints.size >= SESSION_CAPTURE_MAX_ENDPOINTS) return;
      counters = {
        requests: 0,
        errors: 0,
        idleRequests: 0,
        pollingRequests: 0,
        duplicateConcurrentRequests: 0,
        totalDurationMs: 0,
        samplesMs: [],
      };
      endpoints.set(endpoint, counters);
    }
    counters.requests += 1;
    counters.errors += status !== null && (status < 200 || status >= 400) ? 1 : 0;
    counters.idleRequests += idle ? 1 : 0;
    counters.pollingRequests += polling ? 1 : 0;
    counters.duplicateConcurrentRequests += duplicateConcurrent ? 1 : 0;
    counters.totalDurationMs += durationMs;
    remember(counters.samplesMs, durationMs, 128);
    if (idle) interactions.set("idle", (interactions.get("idle") ?? 0) + 1);
  };

  const snapshot = (atMs = now()): SessionCaptureSummary => {
    const elapsedMs = Math.max(1_000, Math.min(durationMs, atMs - startedAtMs));
    const elapsedMinutes = elapsedMs / 60_000;
    const complete = atMs - startedAtMs >= durationMs;
    const allRecords = requests;
    const errors = totalErrors;
    const idleRequests = totalIdleRequests;
    const pollingRequests = totalPollingRequests;
    const duplicateRequests = totalDuplicateConcurrentRequests;
    const latencies = allRecords.map((record) => record.durationMs);
    const interactionCounts = Object.fromEntries(
      [...interactions.entries()].sort((left, right) => right[1] - left[1]),
    );
    const endpointSummary = Object.fromEntries(
      [...endpoints.entries()]
        .sort((left, right) => right[1].requests - left[1].requests)
        .map(([endpoint, counters]) => [
          endpoint,
          {
            requests: counters.requests,
            errors: counters.errors,
            errorRate: counters.requests === 0 ? 0 : counters.errors / counters.requests,
            idleRequests: counters.idleRequests,
            pollingRequests: counters.pollingRequests,
            duplicateConcurrentRequests: counters.duplicateConcurrentRequests,
            latencyMs: latencySummary(counters.samplesMs),
          },
        ]),
    );
    return {
      version: SESSION_CAPTURE_VERSION,
      durationSeconds: Number((elapsedMs / 1_000).toFixed(3)),
      complete,
      requests: totalRequests,
      errors,
      errorRate: totalRequests === 0 ? 0 : errors / totalRequests,
      requestsPerSessionMinute: totalRequests / elapsedMinutes,
      pageTransitions,
      pageTransitionsPerMinute: pageTransitions / elapsedMinutes,
      interactions: [...interactions.values()].reduce((sum, value) => sum + value, 0),
      interactionsPerMinute:
        [...interactions.values()].reduce((sum, value) => sum + value, 0) / elapsedMinutes,
      idleRequests,
      idleRequestsPerMinute: idleRequests / elapsedMinutes,
      pollingRequests,
      pollingRequestsPerMinute: pollingRequests / elapsedMinutes,
      duplicateConcurrentRequests: duplicateRequests,
      duplicateConcurrentRate:
        totalRequests === 0 ? 0 : duplicateRequests / totalRequests,
      latencyMs: latencySummary(latencies),
      interactionsByType: interactionCounts,
      pageCategories: Object.fromEntries(pageCategories),
      endpoints: endpointSummary,
      capture: {
        eventSamples: requests.length,
        eventSampleLimit: maxEvents,
        requestsObservedFrom: "resource-timing",
        networkSentByCapture: false,
        userIdentifiersStored: false,
      },
    };
  };

  return {
    startedAtMs,
    recordRequest,
    recordPageTransition,
    recordInteraction,
    snapshot,
    stop: () => {
      stopped = true;
    },
  };
}

export function installBrowserSessionCapture(
  options: SessionCaptureOptions = {},
): SessionCaptureController | null {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") {
    return null;
  }
  const capture = createSessionCapture(options);
  const seen = new Set<PerformanceEntry>();
  const captureResource = (entry: PerformanceEntry): void => {
    if (seen.has(entry)) return;
    seen.add(entry);
    if (entry.entryType !== "resource") return;
    const resource = entry as PerformanceResourceTiming;
    if (
      resource.initiatorType !== "fetch" &&
      resource.initiatorType !== "xmlhttprequest" &&
      !resource.name.includes("/api/")
    ) {
      return;
    }
    capture.recordRequest({
      url: resource.name,
      startedAtMs: capture.startedAtMs + resource.startTime,
      durationMs: resource.duration,
      pageCategory: pageCategoryFromPath(window.location.pathname),
    });
  };
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) captureResource(entry);
  });
  observer.observe({ type: "resource", buffered: true });
  for (const entry of performance.getEntriesByType("resource")) captureResource(entry);

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  const onNavigation = (): void => {
    capture.recordPageTransition(window.location.pathname);
  };
  history.pushState = function (...args): void {
    originalPushState.apply(history, args);
    onNavigation();
  };
  history.replaceState = function (...args): void {
    originalReplaceState.apply(history, args);
    onNavigation();
  };
  window.addEventListener("popstate", onNavigation);
  const onClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const label = target.closest<HTMLElement>("[data-aero-interaction]")?.dataset.aeroInteraction;
    capture.recordInteraction(label ?? (target.closest("a,button") ? "navigation" : "other"));
  };
  document.addEventListener("click", onClick, { passive: true });
  const timer = window.setTimeout(() => capture.stop(), options.durationMs ?? SESSION_CAPTURE_DURATION_MS);
  const originalStop = capture.stop;
  capture.stop = (): void => {
    window.clearTimeout(timer);
    observer.disconnect();
    window.removeEventListener("popstate", onNavigation);
    document.removeEventListener("click", onClick);
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    originalStop();
  };
  return capture;
}
